import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, ExternalLink, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

type BillingStatus = {
  has_org: boolean;
  subscription_status: string;
  trial_days_left: number;
  trial_ends_at: string | null;
  has_subscription: boolean;
  package_name: string | null;
  price_per_seat: number | null;
  seat_count: number | null;
};

const PLANS = [
  {
    name: "Starter",
    monthlyPrice: 299,
    annualPrice: 249,
    minSeats: 3,
    features: [
      "iPhone field app",
      "Photo temperatures",
      "Turns, recipes, materials",
      "Pile history",
    ],
    monthlyPriceId: "price_1TJEWoLe9qCZBeeNickj2HdQ",
    annualPriceId: "price_1TJEWoLe9qCZBeeNMHaomjb8",
  },
  {
    name: "Professional",
    monthlyPrice: 599,
    annualPrice: 499,
    minSeats: 5,
    popular: true,
    features: [
      "Everything in Starter",
      "BOLs & work orders",
      "Office operations",
      "Pile history for audits",
      "Priority support",
    ],
    monthlyPriceId: "price_1TJEWoLe9qCZBeeNdxBItbye",
    annualPriceId: "price_1TJEWoLe9qCZBeeNWkuLN2mL",
  },
  {
    name: "Enterprise",
    monthlyPrice: 1499,
    annualPrice: null,
    minSeats: 10,
    features: [
      "White-glove setup",
      "API access",
      "Multi-site management",
      "Custom integrations",
      "Dedicated account manager",
    ],
    monthlyPriceId: "price_1TJEWoLe9qCZBeeNccNZBvA5",
    annualPriceId: "",
  },
];

export default function BillingPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  useEffect(() => {
    if (!token) return;
    apiFetch("/api/billing/status", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { setStatus(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  async function handleCheckout(priceId: string) {
    if (!priceId || priceId.startsWith("price_TODO")) {
      setErrorMsg("This plan is not yet available for purchase. Please contact sales.");
      return;
    }
    setCheckoutLoading(priceId);
    setErrorMsg("");
    try {
      const res = await apiFetch("/api/billing/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ priceId, quantity: 1 }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setErrorMsg(data.error || "Failed to start checkout. Please try again.");
      }
    } catch {
      setErrorMsg("Unable to connect. Please check your internet connection and try again.");
    } finally {
      setCheckoutLoading(null);
    }
  }

  async function handleManageSubscription() {
    setPortalLoading(true);
    setErrorMsg("");
    try {
      const res = await apiFetch("/api/billing/customer-portal", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setErrorMsg(data.error || "Failed to open billing portal. Please try again.");
      }
    } catch {
      setErrorMsg("Unable to connect. Please check your internet connection and try again.");
    } finally {
      setPortalLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-muted-foreground">Loading billing info…</div>
      </div>
    );
  }

  const statusIcon = {
    active: <CheckCircle size={16} className="text-green-400" />,
    trialing: <Clock size={16} className="text-forest-400" />,
    past_due: <AlertTriangle size={16} className="text-amber-400" />,
    canceled: <AlertTriangle size={16} className="text-red-400" />,
    expired: <AlertTriangle size={16} className="text-red-400" />,
  }[status?.subscription_status || "trialing"] || <Clock size={16} className="text-muted-foreground" />;

  const statusLabel = {
    active: "Active",
    trialing: "Free Trial",
    past_due: "Past Due",
    canceled: "Canceled",
    expired: "Expired",
  }[status?.subscription_status || "trialing"] || "Unknown";

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Billing</h1>
        <p className="text-muted-foreground mt-1">Manage your subscription and payment details.</p>
      </div>

      {errorMsg && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          {errorMsg}
        </div>
      )}

      {/* Current Plan Card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <CreditCard size={20} />
            Current Plan
          </h2>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary text-sm font-medium">
            {statusIcon}
            {statusLabel}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Plan</p>
            <p className="text-foreground font-medium mt-1">{status?.package_name || (status?.subscription_status === "trialing" ? "Trial" : status?.subscription_status === "active" && !status?.has_subscription ? "Free" : "No plan selected")}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Seats</p>
            <p className="text-foreground font-medium mt-1">{status?.seat_count || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              {status?.subscription_status === "trialing" ? "Trial ends" : "Status"}
            </p>
            <p className="text-foreground font-medium mt-1">
              {status?.subscription_status === "trialing" && status.trial_ends_at
                ? `${status.trial_days_left} days left`
                : statusLabel}
            </p>
          </div>
        </div>

        {status?.has_subscription && (
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium rounded-lg transition-colors"
          >
            <ExternalLink size={14} />
            {portalLoading ? "Opening…" : "Manage Subscription"}
          </button>
        )}
      </div>

      {/* Plan Selection */}
      {(!status?.has_subscription || status?.subscription_status === "trialing" || status?.subscription_status === "expired" || status?.subscription_status === "canceled") && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">
            {status?.subscription_status === "trialing" ? "Subscribe to keep access after your trial" : "Choose a plan"}
          </h2>

          {/* Billing cycle toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
            <button
              onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
              className={`relative w-11 h-6 rounded-full transition-colors ${billingCycle === "annual" ? "bg-forest-600" : "bg-secondary"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${billingCycle === "annual" ? "translate-x-5" : ""}`} />
            </button>
            <span className={`text-sm font-medium ${billingCycle === "annual" ? "text-foreground" : "text-muted-foreground"}`}>
              Annual <span className="text-forest-500 text-xs">(save up to 17%)</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((plan) => {
              const displayPrice = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;
              const activePriceId = billingCycle === "annual" ? plan.annualPriceId : plan.monthlyPriceId;
              const isEnterprise = plan.name === "Enterprise";

              return (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 ${
                    plan.popular
                      ? "border-forest-500 bg-forest-500/5 ring-1 ring-forest-500/20"
                      : "border-border bg-card"
                  }`}
                >
                  {plan.popular && (
                    <span className="inline-block px-2 py-0.5 bg-forest-600 text-white text-xs font-semibold rounded-full mb-3">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                  <div className="mt-2 mb-4">
                    {isEnterprise && billingCycle === "annual" ? (
                      <p className="text-xl font-bold text-foreground">Custom pricing</p>
                    ) : displayPrice ? (
                      <p className="text-foreground">
                        <span className="text-3xl font-bold">${displayPrice.toLocaleString()}</span>
                        <span className="text-muted-foreground text-sm">/mo</span>
                      </p>
                    ) : (
                      <p className="text-xl font-bold text-foreground">Custom pricing</p>
                    )}
                    {billingCycle === "annual" && plan.annualPrice && !isEnterprise && (
                      <p className="text-xs text-muted-foreground mt-1">
                        ${(plan.annualPrice * 12).toLocaleString()}/year billed annually
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {isEnterprise ? "10+ seats included" : `${plan.minSeats} seats included`}
                    </p>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle size={14} className="text-forest-500 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isEnterprise ? (
                    <a
                      href="mailto:rodolfo@bettersystems.ai?subject=CompostDeveloper Enterprise"
                      className="block w-full py-2.5 px-4 bg-secondary hover:bg-secondary/80 text-foreground font-medium rounded-lg transition-colors text-sm text-center"
                    >
                      Contact Sales
                    </a>
                  ) : (
                    <button
                      onClick={() => handleCheckout(activePriceId)}
                      disabled={!!checkoutLoading}
                      className={`w-full py-2.5 px-4 font-medium rounded-lg transition-colors text-sm ${
                        plan.popular
                          ? "bg-forest-600 hover:bg-forest-700 text-white"
                          : "bg-secondary hover:bg-secondary/80 text-foreground"
                      } disabled:opacity-50`}
                    >
                      {checkoutLoading === activePriceId ? "Redirecting…" : "Subscribe"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
